import { COLLECTIONS, loadCollections, readAll, resetCollections } from './collections';
import type { CollectionName } from './collections';
import { primeCounter, primeReference } from './ids';
import { configureRuntime, runtime } from './runtime';
import { createMemoryStore } from './storage';
import { env } from '../config/env';

/**
 * Application bootstrap.
 *
 * Loads the demonstration dataset, primes the id counters above whatever is
 * already stored, and returns whether this was a first run so the shell can
 * tell the user their environment was seeded.
 */

const ID_PREFIXES: [string, Parameters<typeof readAll>[0]][] = [
  ['emp', 'employees'],
  ['dept', 'departments'],
  ['att', 'attendanceRecords'],
  ['lv', 'leaveRequests'],
  ['ot', 'overtimeRequests'],
  ['cor', 'attendanceCorrections'],
  ['cand', 'candidates'],
  ['ast', 'assets'],
  ['aud', 'auditEntries'],
  ['ntf', 'notifications'],
  ['usr', 'systemUsers'],
  ['role', 'roles'],
];

function primeAll(): void {
  for (const [prefix, collection] of ID_PREFIXES) {
    primeCounter(
      prefix,
      readAll<{ id: string }>(collection).map((row) => row.id),
    );
  }
  const year = new Date().getFullYear();
  primeReference(
    'LV',
    year,
    readAll<{ referenceCode?: string }>('leaveRequests').map((r) => r.referenceCode ?? ''),
  );
  primeReference(
    'OT',
    year,
    readAll<{ referenceCode?: string }>('overtimeRequests').map((r) => r.referenceCode ?? ''),
  );
  primeReference(
    'CR',
    year,
    readAll<{ referenceCode?: string }>('attendanceCorrections').map((r) => r.referenceCode ?? ''),
  );
}

/**
 * IndexedDB can block indefinitely rather than failing.
 *
 * A pending `deleteDatabase`, a storage-pressure prompt or Safari's private
 * mode leave `open()` pending forever — no error, no rejection. Without a
 * timeout the application sits on "Preparing your workspace" with nothing to
 * act on, which is the worst kind of failure: it looks like a hang rather than
 * a problem someone can do something about.
 */
const BOOTSTRAP_TIMEOUT_MS = 8_000;

export interface BootstrapResult {
  seeded: boolean;
  /** True when persistence failed and this session is running in memory only. */
  degraded: boolean;
}

/**
 * Which dataset the local collections start from.
 *
 * Mock mode gets the demonstration company; API mode gets empty collections.
 * The collections still exist in API mode because the not-yet-migrated service
 * methods read them — but they must read *nothing*, not a fabricated company.
 * The seed module is imported lazily so its 96KB of fictional data never ships
 * in a bundle configured for the real API.
 */
async function seedBuilder(): Promise<() => Record<CollectionName, unknown[]>> {
  /*
   * The static half of this condition is a compile-time constant, so a
   * production build (other than a deliberate prototype build) contains no
   * reachable `import('./seed')` — the bundler does not even emit the seed
   * chunk to the server.
   */
  const seedAvailable =
    !import.meta.env.PROD || import.meta.env.VITE_ALLOW_MOCK_IN_PRODUCTION === 'true';
  if (seedAvailable && env().dataSource === 'mock') {
    const { buildSeed } = await import('./seed');
    return buildSeed;
  }
  return () => {
    const empty = {} as Record<CollectionName, unknown[]>;
    for (const name of COLLECTIONS) empty[name] = [];
    return empty;
  };
}

export async function bootstrap(): Promise<BootstrapResult> {
  const seed = await seedBuilder();
  try {
    const result = await Promise.race([
      loadCollections(seed),
      new Promise<never>((_resolve, reject) =>
        setTimeout(() => reject(new Error('storage-timeout')), BOOTSTRAP_TIMEOUT_MS),
      ),
    ]);
    primeAll();
    return { ...result, degraded: false };
  } catch {
    // Fall back to an in-memory store: everything works for this session and
    // nothing survives a reload. Far better than a blank screen, and the
    // interface says so rather than pretending the data is being saved.
    configureRuntime({ storage: createMemoryStore() });
    await resetCollections(seed);
    primeAll();
    return { seeded: true, degraded: true };
  }
}

/** Reset the demonstration data. Requires confirmation in the interface. */
export async function resetDemoData(): Promise<void> {
  await resetCollections(await seedBuilder());
  primeAll();
}

/** Apply Simulation Centre settings to the running services. */
export function applySimulation(settings: {
  latencyMs: number;
  failAll: boolean;
  failingServices: string[];
}): void {
  configureRuntime({
    latencyMs: settings.latencyMs,
    failAll: settings.failAll,
    failing: new Set(settings.failingServices),
  });
}

export { runtime };
